/**
 * Pure calendar math used both by the SPA (rendering grids) and the API
 * (allocation cell hours↔percent conversion). No `TODAY`-dependent state
 * lives here — render-time concerns (`ALL_MONTHS`, `CURRENT_MONTH_KEY`)
 * stay in `apps/web/src/tokens.ts` as browser-local UI state.
 *
 * Floating Estonian holidays (Good Friday, Easter Sunday, Pentecost) are
 * computed from the Anonymous Gregorian Easter algorithm, not stored.
 * Admin-managed extras flow in through the `extras` parameter on
 * `workHoursForMonth` so company-wide non-working days reduce capacity
 * end-to-end.
 */
import type { ExtrasList } from "./types";

/** Fallback monthly working hours when the calendar can't resolve a key. */
export const WH = 168;

/** Fixed Estonian public holidays — date-only primitives shared with the SPA. */
const EE_FIXED: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [2, 24],
  [5, 1],
  [6, 23],
  [6, 24],
  [8, 20],
  [12, 24],
  [12, 25],
  [12, 26],
] as const;

/**
 * Anonymous Gregorian Easter (Meeus / Butcher). Returns the (month, day) of
 * Easter Sunday for any year in the Gregorian calendar.
 */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function shiftDate(year: number, month: number, day: number, deltaDays: number) {
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return { month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Good Friday = Easter Sunday − 2 days. */
export function goodFriday(year: number): { month: number; day: number } {
  const e = easterSunday(year);
  return shiftDate(year, e.month, e.day, -2);
}

/** Pentecost = Easter Sunday + 49 days. */
export function pentecost(year: number): { month: number; day: number } {
  const e = easterSunday(year);
  return shiftDate(year, e.month, e.day, 49);
}

export function isEstHoliday(y: number, m: number, d: number): boolean {
  for (const [fm, fd] of EE_FIXED) if (fm === m && fd === d) return true;
  const gf = goodFriday(y);
  if (gf.month === m && gf.day === d) return true;
  const es = easterSunday(y);
  if (es.month === m && es.day === d) return true;
  const pe = pentecost(y);
  if (pe.month === m && pe.day === d) return true;
  return false;
}

const WH_CACHE: Record<string, number> = {};

function extrasFingerprint(extras: ExtrasList): string {
  if (extras.length === 0) return "-";
  const seen = new Set<number>();
  const tuples: number[] = [];
  for (const e of extras) {
    const key = e.month * 100 + e.day;
    if (seen.has(key)) continue;
    seen.add(key);
    tuples.push(key);
  }
  tuples.sort((a, b) => a - b);
  return tuples.join(",");
}

export function workHoursForMonth(y: number, m: number, extras: ExtrasList = []): number {
  const key = `${y}-${m}-${extrasFingerprint(extras)}`;
  const cached = WH_CACHE[key];
  if (cached != null) return cached;
  const extraDays = new Set<number>();
  for (const e of extras) {
    if (e.month !== m) continue;
    extraDays.add(e.day);
  }
  let bdays = 0;
  const daysIn = new Date(y, m, 0).getDate();
  for (let d = 1; d <= daysIn; d++) {
    const dow = new Date(y, m - 1, d).getDay();
    if (dow === 0 || dow === 6) continue;
    if (isEstHoliday(y, m, d)) continue;
    if (extraDays.has(d)) continue;
    bdays++;
  }
  const h = bdays * 8;
  WH_CACHE[key] = h;
  return h;
}

const YM_KEY_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function parseYmKey(key: string): { y: number; m: number } | null {
  const match = YM_KEY_RE.exec(key);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) };
}

export function ymKeyOfDate(date: Date, tz: "utc" | "local"): string {
  const y = tz === "utc" ? date.getUTCFullYear() : date.getFullYear();
  const m = tz === "utc" ? date.getUTCMonth() + 1 : date.getMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

/** Add `n` calendar months to a ymKey. Returns a new ymKey. */
export function addMonthsToYmKey(ymKey: string, n: number): string {
  const parsed = parseYmKey(ymKey);
  if (!parsed) return ymKey;
  const total = parsed.y * 12 + (parsed.m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}
